import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import React, { useCallback } from 'react'

import { cn } from '@/lib/utils'

import { Button } from '../button/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select/select'

const PAGE_SIZE_OPTIONS = [
  { label: '10', value: '10' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
]

interface PaginationInfoProps {
  className?: string
  currentPage: number
  totalPages: number
}

const PaginationInfo = React.forwardRef<HTMLDivElement, PaginationInfoProps>(
  ({ className, currentPage, totalPages }, ref) => {
    return (
      <div ref={ref} className={cn('text-sm font-medium', className)}>
        Page {currentPage} of {totalPages}
      </div>
    )
  }
)
PaginationInfo.displayName = 'PaginationInfo'

interface PaginationControlsProps {
  currentPage: number
  totalPages: number
  disabled?: boolean
  onChange: (page: number) => void
  className?: string
}

const PaginationControls = React.forwardRef<HTMLDivElement, PaginationControlsProps>(
  ({ currentPage, totalPages, disabled = false, onChange, className }, ref) => {
    const handleFirstClick = useCallback(() => {
      onChange(1)
    }, [onChange])

    const handlePreviousClick = useCallback(() => {
      if (currentPage <= 1) {
        return
      }

      onChange(currentPage - 1)
    }, [onChange, currentPage])

    const handleNextClick = useCallback(() => {
      if (currentPage >= totalPages) {
        return
      }

      onChange(currentPage + 1)
    }, [onChange, currentPage, totalPages])

    const handleLastClick = useCallback(() => {
      onChange(totalPages)
    }, [onChange, totalPages])

    return (
      <div ref={ref} className={cn('flex gap-2', className)}>
        <Button
          className="h-8 w-8 p-0"
          variant="outline"
          size="sm"
          disabled={disabled || currentPage <= 1}
          onClick={handleFirstClick}
        >
          <ChevronsLeft size={16} />
        </Button>

        <Button
          className="h-8 w-8 p-0"
          variant="outline"
          size="sm"
          disabled={disabled || currentPage <= 1}
          onClick={handlePreviousClick}
        >
          <ChevronLeft size={14} />
        </Button>

        <Button
          className="h-8 w-8 p-0"
          variant="outline"
          size="sm"
          disabled={disabled || currentPage >= totalPages}
          onClick={handleNextClick}
        >
          <ChevronRight size={14} />
        </Button>

        <Button
          className="h-8 w-8 p-0"
          variant="outline"
          size="sm"
          disabled={disabled || currentPage >= totalPages}
          onClick={handleLastClick}
        >
          <ChevronsRight size={16} />
        </Button>
      </div>
    )
  }
)
PaginationControls.displayName = 'PaginationControls'

interface PageSizeSelectorProps {
  className?: string
  disabled?: boolean
  value?: number
  onChange: (pageSize: number) => void
}

const PageSizeSelector = React.forwardRef<HTMLDivElement, PageSizeSelectorProps>(
  ({ className, disabled = false, value: pageSize, onChange }, ref) => {
    const handleChange = useCallback(
      (value: string) => {
        onChange(parseInt(value))
      },
      [onChange]
    )

    return (
      <div ref={ref} className={cn('flex items-center gap-2', 'text-sm font-medium', className)}>
        <div>Rows per page</div>
        <div>
          <Select disabled={disabled} value={pageSize?.toString() ?? ''} onValueChange={handleChange}>
            <SelectTrigger className="h-9 gap-2.5 text-sm font-normal">
              <SelectValue placeholder="Rows per page" />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map(({ label, value }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    )
  }
)
PageSizeSelector.displayName = 'PageSizeSelector'

export { PaginationControls, PaginationInfo, PageSizeSelector }
